import { useCallback, useEffect, useRef, useState } from "react";

export type ComposerMode = "email" | "telegram";

export interface AttachmentMeta {
  readonly id: string;
  readonly name: string;
  readonly mimeType?: string;
}

export interface ComposerDraft {
  readonly text: string;
  readonly attachments: readonly string[];
  readonly attachmentMeta: readonly AttachmentMeta[];
  readonly revision: number;
}

const STORAGE_KEY = "magnis.composer.drafts.v1";

const EMPTY_DRAFT: ComposerDraft = {
  text: "",
  attachments: [],
  attachmentMeta: [],
  revision: 0,
};

function draftKey(mode: ComposerMode, threadKey: string): string {
  return `${mode}:${threadKey}`;
}

interface PersistedDraft {
  readonly text?: string;
  readonly attachments?: readonly string[];
  readonly attachmentMeta?: readonly AttachmentMeta[];
  readonly revision?: number;
}

function readAll(): Record<string, PersistedDraft> {
  if (typeof localStorage === "undefined") return {};
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, PersistedDraft>;
    }
  } catch (err: unknown) {
    // Corrupt storage: surface the failure instead of silently resetting.
    // Returning {} is the only non-crash option here (throwing would kill
    // every composer on boot until the user clears localStorage by hand),
    // but it MUST be visible — hidden data loss is exactly what CLAUDE.md
    // NO FALLBACKS forbids.
    // eslint-disable-next-line no-console
    console.error("useComposerDraft: corrupt localStorage; resetting drafts", err);
  }
  return {};
}

function writeAll(all: Record<string, PersistedDraft>): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function readOne(mode: ComposerMode, threadKey: string): ComposerDraft {
  const all = readAll();
  const raw = all[draftKey(mode, threadKey)];
  if (!raw) return EMPTY_DRAFT;
  // Normalize drafts persisted before `attachmentMeta` was added.
  return {
    text: raw.text ?? "",
    attachments: raw.attachments ?? [],
    attachmentMeta: raw.attachmentMeta ?? [],
    revision: raw.revision ?? 0,
  };
}

function writeOne(mode: ComposerMode, threadKey: string, draft: ComposerDraft): void {
  const all = readAll();
  all[draftKey(mode, threadKey)] = draft;
  writeAll(all);
}

function deleteOne(mode: ComposerMode, threadKey: string): void {
  const key = draftKey(mode, threadKey);
  const all = readAll();
  const { [key]: _removed, ...rest } = all;
  void _removed;
  writeAll(rest);
}

export interface ComposerDraftPatch {
  readonly text?: string;
  readonly attachments?: readonly string[];
  readonly attachmentMeta?: readonly AttachmentMeta[];
  /** When provided, sets revision instead of bumping it (used by apply handler). */
  readonly revision?: number;
}

/**
 * Non-React helper: write a patch directly to draft storage.
 *
 * Used by callers outside React (e.g. telegram/index.tsx onDraftRequest).
 * Bumps revision unless `patch.revision` is supplied. Returns the new draft.
 */
export function writeDraftDirect(
  mode: ComposerMode,
  threadKey: string,
  patch: ComposerDraftPatch,
): ComposerDraft {
  const current = readOne(mode, threadKey);
  const next: ComposerDraft = {
    text: patch.text ?? current.text,
    attachments: patch.attachments ?? current.attachments,
    attachmentMeta: patch.attachmentMeta ?? current.attachmentMeta,
    revision: patch.revision ?? current.revision + 1,
  };
  writeOne(mode, threadKey, next);
  return next;
}

export interface UseComposerDraft {
  readonly draft: ComposerDraft;
  readonly setText: (text: string) => void;
  readonly setAttachments: (ids: readonly string[], meta?: readonly AttachmentMeta[]) => void;
  readonly clear: () => void;
  /** Apply a revision-bearing patch (e.g. from composer.apply). */
  readonly applyRemote: (patch: ComposerDraftPatch) => void;
  readonly revision: number;
}

export function useComposerDraft(
  mode: ComposerMode,
  threadKey: string,
): UseComposerDraft {
  const [draft, setDraft] = useState<ComposerDraft>(() => readOne(mode, threadKey));
  const keyRef = useRef(draftKey(mode, threadKey));

  useEffect(() => {
    const newKey = draftKey(mode, threadKey);
    if (newKey === keyRef.current) return;
    keyRef.current = newKey;
    setDraft(readOne(mode, threadKey));
  }, [mode, threadKey]);

  const update = useCallback(
    (patch: ComposerDraftPatch) => {
      const next = writeDraftDirect(mode, threadKey, patch);
      setDraft(next);
    },
    [mode, threadKey],
  );

  return {
    draft,
    revision: draft.revision,
    setText: useCallback((text: string) => { update({ text }); }, [update]),
    setAttachments: useCallback(
      (attachments: readonly string[], meta?: readonly AttachmentMeta[]) => {
        update(meta ? { attachments, attachmentMeta: meta } : { attachments });
      },
      [update],
    ),
    applyRemote: useCallback(
      (patch: ComposerDraftPatch) => { update(patch); },
      [update],
    ),
    clear: useCallback(() => {
      deleteOne(mode, threadKey);
      setDraft(EMPTY_DRAFT);
    }, [mode, threadKey]),
  };
}

export const __INTERNAL = { STORAGE_KEY, draftKey };
